import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { PartnerService } from '../../../services/partner.service';
import { ImportService } from '../../../services/import.service';
import { MatPaginator } from '@angular/material/paginator';

import { DataManagementCreateShowModalComponent } from '../../_modals/data-management-create-show-modal/data-management-create-show-modal.component';
import { ShowMapperModalComponent } from '../../_modals/show-mapper-modal/show-mapper-modal.component';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Subject, forkJoin } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { BulkShowRequest, BulkShowResponse } from '../../../models/bulk-show';
import { PartnerGroup } from '../../../models/partner-group';
import { SnackBarService } from '../../../services/snack-bar.service';

interface ShowMapperRow {
  id?: number;
  showName?: string | null;
  networkService?: string | null;
}

interface BulkCreateBatch {
  request: BulkShowRequest;
  titles: string[];
  groupName: string;
  rowCount: number;
}

interface BulkDialogResult {
  title: string;
  succeeded: boolean;
  message: string;
}

@Component({
  selector: 'app-show-mapper-table',
  templateUrl: './show-mapper-table.component.html',
  styleUrls: ['./show-mapper-table.component.scss']
})
export class ShowMapperTableComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('bulkDialog') bulkDialog!: TemplateRef<unknown>;
  uniqueNetworkList: string[] = [];
  selectedNetwork = '';
  networkSearch = '';

  get filteredNetworks(): string[] {
    const search = this.networkSearch.trim().toLowerCase();
    return this.uniqueNetworkList.filter(network => network.toLowerCase().includes(search));
  }
  
  displayedColumns: string[] = [
    'select',
    'title',
    'network',
    'action'
    // 'artist__user__first_name',
    // 'genre',
    // 'sub_genre',
    // 'created_at',
    // 'updated_at',
    // 'approved_at',
    // 'status',
    // 'action',
  ];

  // tableData: MatTableDataSource<any>;
  
  tableData = new MatTableDataSource<ShowMapperRow>();
  profileId: any;
  isFetchingTableData = false;

  searchFilterFocused = false;
  search: any = '';

  proShows: any;
  partnerGroups: PartnerGroup[] = [];
  selectedPartnerGroupId: number | null = null;
  selectedRows = new Set<ShowMapperRow>();
  rowErrors = new Map<string, string>();
  createdTitles = new Set<string>();
  pendingBatch: BulkCreateBatch | null = null;
  dialogBatch: BulkCreateBatch | null = null;
  bulkDialogResults: BulkDialogResult[] = [];
  bulkDialogFinished = false;
  private bulkDialogRef: MatDialogRef<unknown> | null = null;
  isBulkSaving = false;
  bulkMessage = '';
  bulkError = '';
  bulkResultUncertain = false;
  private readonly destroyed$ = new Subject<void>();

  // searchCategory;
  searchCategory = 'All';
  searchCategories = [
    'All',
    'Title',
    'Network'
  ];

  constructor(
    private partnerService: PartnerService,
    private importService: ImportService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private snackBar: SnackBarService,
    private viewContainerRef: ViewContainerRef
  ) {
  }

  ngOnInit() {

    // set profileID off the URL parameter
    // other importService subscription stuff is here for Robert's pieces until those can be undone
    this.route.queryParams.pipe(takeUntil(this.destroyed$)).subscribe(params => {
      this.profileId = params['profileId'];
      console.log('Profile ID:', this.profileId);

      if (this.profileId) {
        this.importService.getImportProMappedHeadersProfile(this.profileId).subscribe((x) => {
          this.importService.setImportProMappedHeadersProfile(x);

          //this needs to be set for the header to display the appropriate buttons
          this.importService.setImportedFilePath(x.filePath);

        })
      }
    });

    // reload table when a modal closes (WITH SUCCESS, not canceled)
    this.partnerService.modalClosed$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.reloadShows();
    });

    this.reloadShows();

    this.partnerService.getPartnerGroups().pipe(takeUntil(this.destroyed$)).subscribe(groups => {
      this.partnerGroups = groups;
    }, () => {
      this.reportBulkError('Partner groups could not be loaded. Refresh the page before creating shows.');
    });

  }

  ngAfterViewInit() {
    this.tableData.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    if (this.bulkDialogRef) {
      this.bulkDialogRef.close();
    }
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private reloadShows(): void {
    this.isFetchingTableData = true;
    forkJoin({
      imports: this.importService.getImportDetails(this.profileId, 1),
      shows: this.partnerService.getAllPROShows()
    }).pipe(
      takeUntil(this.destroyed$),
      finalize(() => {
        this.isFetchingTableData = false;
        this.updateBulkDialogClosing();
      })
    ).subscribe(({ imports, shows }) => {
      this.setTableData(imports);
      this.proShows = shows;
    }, () => {
      this.reportBulkError('The show lists could not be refreshed. Saved shows were not rolled back. '
        + 'Refresh the page before trying again.');
    });
  }

  // getSearchVal(event: any){
  //   this.searchString = event.trim();
  // }

  private setTableData(data: ShowMapperRow[]): void {
    const selectedKeys = new Set(Array.from(this.selectedRows).map(row => this.rowKey(row)));
    this.selectedRows = new Set(data.filter(row => selectedKeys.has(this.rowKey(row)) && this.canSelectRow(row)));
    this.tableData = new MatTableDataSource<ShowMapperRow>(data);
    this.uniqueNetworkList = Array.from(new Set<string>(
      data.map(item => item.networkService).filter((network): network is string => typeof network === 'string' && network.length > 0)
    )).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    this.tableData.paginator = this.paginator;
    this.applyFilter();
  }

  filterData(search: string): void {
    if (!this.tableData) {
      return;
    }

    const searchTerm = (search || '').trim().toLowerCase();
    const category = this.searchCategory;
    const network = this.selectedNetwork;
    const column = category === 'Title' ? 'showName' : 'networkService';

    this.tableData.filterPredicate = (data: ShowMapperRow) => {
      if (network && data.networkService !== network) {
        return false;
      }
      if (!searchTerm || !category) {
        return true;
      }
      if (category === 'All') {
        return Object.values(data).some(fieldValue =>
          fieldValue && fieldValue.toString().toLowerCase().includes(searchTerm)
        );
      }
      const value = data[column];
      return !!value && value.toString().toLowerCase().includes(searchTerm);
    };

    this.tableData.filter = searchTerm || network
      ? JSON.stringify({ searchTerm, category, network })
      : '';
    this.selectedRows.forEach(row => {
      if (!this.tableData.filteredData.includes(row)) {
        this.selectedRows.delete(row);
      }
    });
    this.pendingBatch = null;
    if (this.tableData.paginator) {
      this.tableData.paginator.firstPage();
    }
  }

  applyFilter(): void {
    this.filterData(this.search); // Apply the filter based on current input and category
  }

  titleKey(row: ShowMapperRow): string {
    return (row.showName || '').trim().toLowerCase();
  }

  private rowKey(row: ShowMapperRow): string {
    return row.id ? `import:${row.id}` : JSON.stringify([row.showName, row.networkService]);
  }

  canSelectRow(row: ShowMapperRow): boolean {
    return !!this.titleKey(row) && !this.createdTitles.has(this.titleKey(row));
  }

  get selectableRows(): ShowMapperRow[] {
    return this.tableData.filteredData.filter(row => this.canSelectRow(row));
  }

  get allFilteredSelected(): boolean {
    return this.selectableRows.length > 0 && this.selectableRows.every(row => this.selectedRows.has(row));
  }

  toggleRow(row: ShowMapperRow): void {
    if (this.isBulkSaving || this.isFetchingTableData || this.bulkResultUncertain || !this.canSelectRow(row)) {
      return;
    }
    if (this.selectedRows.has(row)) {
      this.selectedRows.delete(row);
    } else {
      this.selectedRows.add(row);
    }
    this.pendingBatch = null;
  }

  toggleAllFiltered(): void {
    if (this.isBulkSaving || this.isFetchingTableData || this.bulkResultUncertain) {
      return;
    }
    if (this.allFilteredSelected) {
      this.clearSelection();
    } else {
      this.selectableRows.forEach(row => this.selectedRows.add(row));
      this.pendingBatch = null;
    }
  }

  clearSelection(): void {
    if (!this.isBulkSaving) {
      this.selectedRows.clear();
      this.pendingBatch = null;
    }
  }

  get canBulkCreate(): boolean {
    return this.selectedRows.size > 0 && typeof this.selectedPartnerGroupId === 'number'
      && Number.isInteger(this.selectedPartnerGroupId)
      && this.selectedPartnerGroupId > 0 && !this.isBulkSaving
      && !this.isFetchingTableData && !this.bulkResultUncertain;
  }

  prepareBulkCreate(): void {
    if (this.bulkDialogRef) {
      return;
    }
    if (!this.canBulkCreate) {
      this.reportBulkError('Select at least one show and a valid partner group before creating shows.');
      return;
    }
    const group = this.partnerGroups.find(item => item.id === this.selectedPartnerGroupId);
    if (!group || typeof group.id !== 'number' || !Number.isInteger(group.id) || group.id <= 0) {
      this.reportBulkError('The selected partner group is unavailable. Refresh the page and select a group again.');
      return;
    }
    const titles = new Map<string, string>();
    this.tableData.filteredData.filter(row => this.selectedRows.has(row)).forEach(row => {
      const key = this.titleKey(row);
      if (!titles.has(key)) {
        titles.set(key, (row.showName || '').trim());
      }
    });
    if (!titles.size || titles.has('')) {
      this.reportBulkError('Every selected show must have a non-empty title.');
      return;
    }
    this.bulkError = '';
    this.bulkMessage = '';
    this.pendingBatch = {
      request: {
        partnerGroup_id: group.id,
        shows: Array.from(titles.values()).map(title => ({ title, description: '' }))
      },
      titles: Array.from(titles.keys()),
      groupName: group.name,
      rowCount: this.selectedRows.size
    };
    this.dialogBatch = this.pendingBatch;
    this.bulkDialogResults = [];
    this.bulkDialogFinished = false;
    this.bulkDialogRef = this.dialog.open(this.bulkDialog, {
      width: '640px',
      maxWidth: '95vw',
      ariaLabel: 'Bulk show creation',
      autoFocus: false,
      viewContainerRef: this.viewContainerRef
    });
    this.bulkDialogRef.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.pendingBatch = null;
      this.dialogBatch = null;
      this.bulkDialogRef = null;
    });
  }

  closeBulkDialog(): void {
    if (this.bulkDialogRef && !this.isBulkSaving && !this.isFetchingTableData) {
      this.bulkDialogRef.close();
    }
  }

  private updateBulkDialogClosing(): void {
    if (this.bulkDialogRef) {
      this.bulkDialogRef.disableClose = this.isBulkSaving || this.isFetchingTableData;
    }
  }

  confirmBulkCreate(): void {
    if (!this.pendingBatch || !this.canBulkCreate) {
      return;
    }
    const batch = this.pendingBatch;
    this.pendingBatch = null;
    this.isBulkSaving = true;
    this.updateBulkDialogClosing();
    this.bulkError = '';
    this.rowErrors.clear();
    this.partnerService.bulkUpdateProShows(batch.request).pipe(
      takeUntil(this.destroyed$),
      finalize(() => {
        this.isBulkSaving = false;
        this.bulkDialogFinished = true;
        this.updateBulkDialogClosing();
      })
    ).subscribe(response => {
      this.handleBulkResponse(response, batch);
    }, (error: HttpErrorResponse) => {
      if (error.status === 400 && error.error && Array.isArray(error.error.results)) {
        this.handleBulkResponse(error.error, batch);
      } else if (error.status === 401 || error.status === 403) {
        this.reportBulkError('Your session has expired or you do not have permission to create shows.');
      } else if (error.status === 400) {
        this.reportBulkError(`The bulk create could not be submitted. ${this.formatErrors(error.error)}`);
      } else {
        this.bulkResultUncertain = true;
        this.reportBulkError('The bulk create could not be completed. No retry was performed automatically. Some shows may have been saved; refresh and check the show list before retrying.');
      }
    });
  }

  private handleBulkResponse(response: BulkShowResponse, batch: BulkCreateBatch): void {
    const results = response && response.results;
    if (!Array.isArray(results) || results.length !== batch.titles.length
      || response.partnerGroup_id !== batch.request.partnerGroup_id
      || results.some(result => !result || !Number.isInteger(result.index)
        || result.index < 0 || result.index >= batch.titles.length || !Number.isInteger(result.status))
      || new Set(results.map(result => result.index)).size !== results.length
      || response.succeeded !== results.filter(result => result.status === 200 || result.status === 201).length
      || response.failed !== results.filter(result => result.status !== 200 && result.status !== 201).length) {
      this.bulkResultUncertain = true;
      this.reportBulkError('The server returned unexpected bulk results. Some shows may have been saved; refresh and check the show list before retrying.');
      return;
    }
    let succeeded = 0;
    results.forEach(result => {
      const title = batch.titles[result.index];
      if (result.status === 200 || result.status === 201) {
        succeeded++;
        this.createdTitles.add(title);
        this.selectedRows.forEach(row => {
          if (this.titleKey(row) === title) {
            this.selectedRows.delete(row);
          }
        });
        this.bulkDialogResults = results.slice().sort((a, b) => a.index - b.index).map(result => {
          const succeededResult = result.status === 200 || result.status === 201;
          return {
            title: batch.request.shows[result.index].title || batch.titles[result.index],
            succeeded: succeededResult,
            message: succeededResult ? 'Created successfully' : this.rowErrors.get(batch.titles[result.index]) || ''
          };
        });
      } else {
        const detail = this.formatErrors(result.errors);
        this.rowErrors.set(title, `${this.resultError(result.status)}${detail ? ' ' + detail : ''}`);
      }
    });
    const failed = results.length - succeeded;
    this.bulkMessage = `${succeeded} show(s) created for ${batch.groupName}. ${failed} failed.`;
    if (failed) {
      this.reportBulkError('Review the errors on the selected rows. Failed shows were not retried automatically.');
    }
    if (succeeded) {
      this.reloadShows();
    }
  }

  private resultError(status: number): string {
    if (status === 404) {
      return 'This show no longer exists. Refresh the list and try again.';
    }
    if (status === 409) {
      return 'This show changed elsewhere. Refresh the list and try again.';
    }
    return 'This show could not be saved. Review its title and partner group.';
  }

  private formatErrors(errors: unknown): string {
    if (!errors || typeof errors !== 'object') {
      return typeof errors === 'string' ? errors : '';
    }
    return Object.entries(errors).map(([field, messages]) =>
      `${field}: ${Array.isArray(messages) ? messages.join(' ') : String(messages)}`
    ).join(' ');
  }

  private reportBulkError(message: string): void {
    this.bulkError = message;
    if (!this.bulkDialogRef) {
      this.snackBar.showSnackBarMessage(message, 'Dismiss', 8000, true);
    }
  }

  openCreateShowModal(show: any) {
    let dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: show.showName,
      partnerGroups: this.partnerGroups
    };
    this.dialog.open(DataManagementCreateShowModalComponent, dialogConfig);
  }

  openMapShowModal(show: any) {
    let dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      // title: show.showName,
      importedProShow: show,
      shows: this.proShows
    };
    this.dialog.open(ShowMapperModalComponent, dialogConfig);
  }

}
